import React, { useEffect, useCallback, useState, useRef } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Container, Button } from 'react-bootstrap';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import { formatPrice } from '../../../utils/funcoes';
import getValidationErrors from '../../../utils/getValidationErrors';

import SelectSync from '../../SelectSync';
import TextArea from '../../TextArea';
import Loading from '../../Loading';
import { ICartaoSaude, IParcelas } from '../../../pages/CartaoSaudeDetalhe';
import getResponseErrors from '../../../utils/getResponseErrors';

type ComponentProps = {
  PlaCodigo: number;
  PlaRenovado: boolean;
  PlaValor: number;
  PlaDataInicio: Date;
  updated_at: Date;
  parcelas: IParcelas[];
  CartaoNovo: boolean;
  handleCancelouCartao(dados: ICartaoSaude): void;
  handleFechar(): void;
};

interface ISubmitForm {
  MotCodigo: string;
  MotDescricao: string;
}

interface IOptions {
  value: string;
  label: string;
}

const CartaoSaudeCancelar: React.FC<ComponentProps> = ({
  PlaCodigo,
  PlaRenovado,
  PlaValor,
  PlaDataInicio,
  updated_at,
  CartaoNovo,
  parcelas,
  handleCancelouCartao,
  handleFechar,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mensagemCancelamento, setMensagemCancelamento] = useState('');
  const [optionsMotivo, setOptionsMotivo] = useState<IOptions[]>([]);
  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    async function loadMotivos(): Promise<void> {
      try {
        const response = await api.get<
          { MotCodigo: number; MotMotivo: string }[]
        >('motivo?MotTipo=2&MotAtivo=true');

        setOptionsMotivo(
          response.data.map(mot => {
            return { value: mot.MotCodigo.toString(), label: mot.MotMotivo };
          }),
        );
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar motivos',
          type: 'error',
          description: mensagemErro,
        });
      }
    }
    loadMotivos();
    const parcelasAberto = parcelas.filter(
      parc =>
        !parc.CprPago &&
        parc.CprParcelaAtiva &&
        !parc.CprExcluida &&
        parc.CprNParcela < 13,
    );

    const geraMulta = !CartaoNovo && parcelasAberto && !PlaRenovado;
    if (geraMulta) {
      const valorMulta = (PlaValor * parcelasAberto.length * 30) / 100;
      setMensagemCancelamento(
        `O cancelamento irá gerar uma multa de ${formatPrice(
          valorMulta,
        )}, deseja continuar?`,
      );
    } else {
      setMensagemCancelamento(
        'Cancelamento sem cobrança de multa, deseja prosseguir?',
      );
    }
  }, [parcelas, PlaDataInicio, PlaValor, PlaRenovado, CartaoNovo, addToast]);

  const handleSubmitForm = useCallback(
    async (dados: ISubmitForm) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          MotCodigo: Yup.string().required('Motivo obrigatório'),
          MotDescricao: Yup.string().required('Descrição obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const { MotCodigo, MotDescricao } = dados;
        const response = await api.put<ICartaoSaude>(
          `planos/cancelar/${PlaCodigo}`,
          {
            MotCodigo: Number(MotCodigo),
            MotDescricao: MotDescricao.toUpperCase(),
            updated_at,
          },
        );

        handleCancelouCartao(response.data);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível salvar os dependentes',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [PlaCodigo, updated_at, handleCancelouCartao, addToast],
  );

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}

      <Form ref={formRef} onSubmit={handleSubmitForm}>
        <h5>{mensagemCancelamento}</h5>
        <SelectSync
          label="Motivo"
          name="MotCodigo"
          options={optionsMotivo}
          handleSelectChange={() => console.log('')}
        />
        <TextArea label="Descrição" name="MotDescricao" rows={5} />
        <Button
          type="submit"
          className="mt-2"
          variant="danger"
          disabled={loading}
        >
          Prosseguir com cancelamento
        </Button>
        <Button
          type="button"
          className="mt-2 ml-3"
          variant="outline-secondary"
          onClick={handleFechar}
          disabled={loading}
        >
          Voltar
        </Button>
      </Form>
    </Container>
  );
};

export default CartaoSaudeCancelar;
